import type { ReactElement } from "react";
import type { LucideIcon } from "lucide-react";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HelpCircle,
  BookOpen,
  Sparkles,
  CreditCard,
  User,
  Wrench,
  MessageCircle,
  ChevronRight,
  Search,
  X,
} from "lucide-react";
import PageLayout from "../components/PageLayout";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

/**
 * FAQ item
 */
interface FaqItem {
  question: string;
  answer: string;
}

/**
 * FAQ category
 */
interface FaqCategory {
  id: string;
  title: string;
  icon: LucideIcon;
  color: string;
  faqs: FaqItem[];
}

const FAQ_CATEGORIES: FaqCategory[] = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: BookOpen,
    color: "#10B981",
    faqs: [
      {
        question: "How do I create my first story?",
        answer:
          'Click "Create" in the navigation, enter a story prompt describing your idea (e.g., "A brave girl who finds a magic garden"), select an age range and art style, then click "Create Story". Our AI will generate a complete illustrated storybook for you!',
      },
      {
        question: "Do I need an account to create stories?",
        answer:
          "You can preview story text without an account, but to generate full illustrated stories and save them to your library, you'll need to sign up. Creating an account is free and gives you 1 free credit to start!",
      },
      {
        question: "What types of stories can I create?",
        answer:
          "You can create picture books (2-4 age range), early readers (5-7), chapter books (8-10), and young adult stories (11+). Each type is optimized for the reading level and attention span of that age group.",
      },
      {
        question: "How long does it take to generate a story?",
        answer:
          "Most stories take 5-15 minutes to generate, with an average around 9-10 minutes. Longer stories and higher quality versions (v2/v3) may take longer. You'll see real-time progress as pages are created.",
      },
    ],
  },
  {
    id: "story-creation",
    title: "Story Creation",
    icon: Sparkles,
    color: "#8B5CF6",
    faqs: [
      {
        question: "What are story versions (v1, v2, v3)?",
        answer:
          "Story versions indicate the AI image quality: v1 (Standard) uses Flux AI, v2 (Premium) uses Gemini 2.5 with character consistency, and v3 (Pro) uses Gemini 3 Pro for the highest quality illustrations. Free users can access v1. Users who purchase credits unlock v2. Premium subscribers can access all versions including v3.",
      },
      {
        question: 'What is an "art style" and which should I choose?',
        answer:
          "Art style determines the visual look of your illustrations. Options include Watercolor (soft, dreamy), Storybook (classic children's book style), Cartoon (fun and vibrant), Anime (Japanese-inspired), and more. Choose based on your personal preference or what fits your story's mood.",
      },
      {
        question: "Can I edit my story after it's created?",
        answer:
          "Currently, stories cannot be edited after generation. We recommend being specific with your prompt to get the best results. If you're not happy with a story, you can create a new one with a refined prompt.",
      },
      {
        question: "What makes a good story prompt?",
        answer:
          'Good prompts are specific and include key details: main character, setting, and what happens. For example, "A shy purple dragon named Violet learns to make friends at a forest school" is better than just "a dragon story".',
      },
      {
        question: "Can I include my child's name in the story?",
        answer:
          'Include the character name in your prompt! For example: "A curious boy named Max discovers a secret door in his grandmother\'s attic". The AI will use this name throughout the story.',
      },
      {
        question: 'What is the "Photo Transform" feature?',
        answer:
          "Photo Transform (Premium feature) lets you upload a photo of a real person, and our AI will turn them into an illustrated character that appears consistently throughout your story. Perfect for making your child the hero of their own book!",
      },
    ],
  },
  {
    id: "premium-features",
    title: "Premium Features",
    icon: CreditCard,
    color: "var(--st-primary)",
    faqs: [
      {
        question: "What do I get with Premium?",
        answer:
          "Premium subscribers get: monthly credits that roll over, audio narration (Read to Me), Photo Transform to include real people as characters, access to Book Series for creating recurring characters, access to v3 Pro image quality, pro picture book layouts, and print discounts (10-25% depending on plan).",
      },
      {
        question: "What are Book Series?",
        answer:
          "Book Series let you create recurring characters that appear consistently across multiple stories. Create a character once, and they'll look the same in every book you create with them. Build epic adventures that span entire collections!",
      },
      {
        question: "What is Audio Narration?",
        answer:
          "Audio Narration (Read to Me) adds professional AI voice narration to your stories. Perfect for bedtime stories or when children want to enjoy stories independently. Available for Premium subscribers.",
      },
      {
        question: "How much does a Pro story (v3) cost?",
        answer:
          "Pro stories (v3) cost 2 credits for the highest quality AI illustrations. Only premium subscribers can access v3 - it's not available to free users or credit-only purchasers.",
      },
      {
        question: "Can I cancel my subscription anytime?",
        answer:
          "Yes! You can cancel your subscription anytime. You'll keep access to premium features until your billing period ends, and any unused credits stay in your account.",
      },
    ],
  },
  {
    id: "account-billing",
    title: "Account & Billing",
    icon: User,
    color: "#3B82F6",
    faqs: [
      {
        question: "What is a credit?",
        answer:
          "One credit = one complete story with illustrations, a cover page, and optional audio narration (for premium users). v1 and v2 stories cost 1 credit. v3 Pro stories cost 2 credits.",
      },
      {
        question: "Do credits expire?",
        answer:
          "No! Credits never expire. Credit pack purchases and subscription credits both roll over — use them whenever you're ready.",
      },
      {
        question: "What happens if I switch plans?",
        answer:
          "Upgrades are charged the prorated difference immediately. Downgrades apply a credit to your next invoice. Your existing credits are always preserved.",
      },
      {
        question: "What subscription plans are available?",
        answer:
          "We offer: Lite ($5.99/mo, 2 credits), Standard ($11.99/mo, 5 credits), Family ($19.99/mo, 10 credits), Creator ($39.99/mo, 20 credits), and Pro Creator ($59.99/mo, 35 credits). All plans include audio narration, photo transform, and print discounts.",
      },
      {
        question: "What credit packs can I buy?",
        answer:
          "Single Story ($3.99, 1 credit), Starter Pack ($9.99, 4 credits), Bundle Pack ($18.99, 8 credits), and Value Pack ($29.99, 15 credits). Purchased credits never expire and unlock v2 Premium image quality.",
      },
      {
        question: "How do I change my email or password?",
        answer:
          'Go to your Profile page and click "Edit Profile" to update your email. For password changes, click "Change Password". If you signed up with Google, you\'ll need to manage your password through Google.',
      },
      {
        question: "How do I delete my account?",
        answer:
          "Contact us through the Contact page to request account deletion. We'll remove your account and all associated data within 30 days as required by privacy regulations.",
      },
      {
        question: "What payment methods do you accept?",
        answer:
          "We accept all major credit cards (Visa, Mastercard, American Express) and debit cards through our secure payment processor, Stripe.",
      },
    ],
  },
  {
    id: "technical",
    title: "Technical Issues",
    icon: Wrench,
    color: "#EF4444",
    faqs: [
      {
        question: "My story generation seems stuck. What should I do?",
        answer:
          "Story generation typically takes 5-15 minutes depending on length and quality settings. If it's been more than 20 minutes, try refreshing the page. If the issue persists, the story will appear in your Library once complete. Contact support if problems continue.",
      },
      {
        question: "Images aren't loading properly. How can I fix this?",
        answer:
          "Try refreshing the page or clearing your browser cache. Make sure you have a stable internet connection. If images still don't load, try a different browser or contact support.",
      },
      {
        question: "The app is running slowly. What can I do?",
        answer:
          "Close other browser tabs, clear your browser cache, and make sure your browser is up to date. If the issue continues, try a different browser (we recommend Chrome, Safari, or Firefox).",
      },
      {
        question: "Can I access my stories on multiple devices?",
        answer:
          "Yes! Your stories are stored in the cloud and accessible from any device. Simply log in with the same account on your phone, tablet, or computer.",
      },
    ],
  },
];

/**
 * HelpPage Component
 *
 * FAQ page organized by categories with search and accordion.
 */
const HelpPage = (): ReactElement => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Filter FAQs based on search
  const filteredCategories = searchQuery.trim()
    ? FAQ_CATEGORIES.map((category) => ({
        ...category,
        faqs: category.faqs.filter(
          (faq) =>
            faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
        ),
      })).filter((category) => category.faqs.length > 0)
    : FAQ_CATEGORIES;

  const totalResults = filteredCategories.reduce(
    (acc, cat) => acc + cat.faqs.length,
    0
  );

  return (
    <PageLayout>
      <div className="min-h-screen pb-16" style={{ background: "var(--st-bg-page)" }}>
        {/* Page Header Card */}
        <div className="max-w-3xl mx-auto px-4 pt-8">
          <div
            className="rounded-2xl p-6 mb-6"
            style={{
              background: "var(--st-bg-surface)",
              border: "1px solid var(--st-border-light)",
              boxShadow: "0 4px 12px rgba(92, 64, 51, 0.06)",
            }}
          >
            <div className="flex flex-col items-center text-center">
              <div
                className="w-16 h-16 mb-4 rounded-2xl flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
                }}
              >
                <HelpCircle className="w-8 h-8 text-white" />
              </div>
              <h1
                className="text-2xl md:text-3xl font-bold mb-2"
                style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
              >
                Help & FAQ
              </h1>
              <p
                className="text-sm max-w-md"
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
              >
                Find answers to common questions about creating stories
              </p>

              {/* Search Bar */}
              <div className="w-full max-w-md mt-5">
                <div
                  className="relative flex items-center"
                  style={{
                    background: "var(--st-bg-page)",
                    border: "1px solid var(--st-border-light)",
                    borderRadius: "12px",
                  }}
                >
                  <Search
                    className="w-4 h-4 absolute left-3"
                    style={{ color: "var(--st-text-muted)" }}
                  />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search for a question..."
                    className="w-full py-3 pl-10 pr-10 rounded-xl text-sm outline-none bg-transparent"
                    style={{
                      fontFamily: "Quicksand, sans-serif",
                      color: "var(--st-text-heading)",
                    }}
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 p-1 rounded-full transition-colors hover:opacity-80"
                      style={{ background: "transparent" }}
                    >
                      <X className="w-4 h-4" style={{ color: "var(--st-text-muted)" }} />
                    </button>
                  )}
                </div>
                {searchQuery && (
                  <p
                    className="text-xs mt-2"
                    style={{
                      fontFamily: "Quicksand, sans-serif",
                      color: "var(--st-text-muted)",
                    }}
                  >
                    {totalResults} result{totalResults !== 1 ? "s" : ""} found
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Jump Categories */}
        {!searchQuery && (
          <div className="max-w-3xl mx-auto px-4 mb-6">
            <div className="flex flex-wrap justify-center gap-2">
              {FAQ_CATEGORIES.map((category) => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => {
                      document
                        .getElementById(category.id)
                        ?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition-all hover:scale-[1.02]"
                    style={{
                      background: "var(--st-bg-surface)",
                      border: "1px solid var(--st-border-light)",
                      fontFamily: "Quicksand, sans-serif",
                      color: "var(--st-text-heading)",
                    }}
                  >
                    <Icon className="w-3.5 h-3.5" style={{ color: category.color }} />
                    {category.title}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* FAQ Categories */}
        <div className="max-w-3xl mx-auto px-4">
          {filteredCategories.length === 0 ? (
            <div
              className="rounded-xl p-8 text-center"
              style={{ background: "var(--st-bg-surface)", border: "1px solid var(--st-border-light)" }}
            >
              <Search
                className="w-12 h-12 mx-auto mb-3"
                style={{ color: "var(--st-border-light)" }}
              />
              <p
                className="font-bold mb-1"
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-heading)" }}
              >
                No results found
              </p>
              <p
                className="text-sm mb-4"
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
              >
                Try a different search term or browse all categories
              </p>
              <button
                onClick={() => setSearchQuery("")}
                className="px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:scale-[1.02]"
                style={{
                  background: "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
                  color: "white",
                  fontFamily: "Quicksand, sans-serif",
                }}
              >
                Clear Search
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredCategories.map((category) => {
                const Icon = category.icon;
                return (
                  <div
                    key={category.id}
                    id={category.id}
                    className="rounded-xl overflow-hidden scroll-mt-4"
                    style={{ background: "var(--st-bg-surface)", border: "1px solid var(--st-border-light)" }}
                  >
                    {/* Category Header */}
                    <div
                      className="px-5 py-4 flex items-center gap-3"
                      style={{ borderBottom: "1px solid var(--st-border-light)" }}
                    >
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ background: `${category.color}15` }}
                      >
                        <Icon
                          className="w-5 h-5"
                          style={{ color: category.color }}
                        />
                      </div>
                      <div>
                        <h2
                          className="text-lg font-bold"
                          style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
                        >
                          {category.title}
                        </h2>
                        <p
                          className="text-xs"
                          style={{
                            fontFamily: "Quicksand, sans-serif",
                            color: "var(--st-text-muted)",
                          }}
                        >
                          {category.faqs.length} question
                          {category.faqs.length !== 1 ? "s" : ""}
                        </p>
                      </div>
                    </div>

                    {/* FAQ Accordion */}
                    <Accordion type="single" collapsible className="px-5">
                      {category.faqs.map((faq, index) => (
                        <AccordionItem
                          key={index}
                          value={`${category.id}-${index}`}
                          style={{ borderColor: "var(--st-border-light)" }}
                        >
                          <AccordionTrigger
                            className="text-left py-4 hover:no-underline"
                            style={{
                              fontFamily: "Quicksand, sans-serif",
                              fontWeight: 600,
                              color: "var(--st-text-heading)",
                            }}
                          >
                            {faq.question}
                          </AccordionTrigger>
                          <AccordionContent>
                            <p
                              className="text-sm leading-relaxed pb-2"
                              style={{
                                fontFamily: "Quicksand, sans-serif",
                                color: "var(--st-text-muted)",
                              }}
                            >
                              {faq.answer}
                            </p>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Contact CTA */}
        <div className="max-w-3xl mx-auto px-4 mt-10">
          <div
            className="rounded-2xl p-6 text-center"
            style={{
              background: "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
            }}
          >
            <MessageCircle className="w-10 h-10 text-white mx-auto mb-3" />
            <h3
              className="text-xl font-bold text-white mb-2"
              style={{ fontFamily: "Chewy, cursive" }}
            >
              Still Have Questions?
            </h3>
            <p
              className="text-white/90 text-sm mb-4"
              style={{ fontFamily: "Quicksand, sans-serif" }}
            >
              Can't find what you're looking for? We're here to help!
            </p>
            <button
              onClick={() => navigate("/contact")}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all hover:scale-[1.02]"
              style={{
                background: "var(--st-bg-surface)",
                color: "var(--st-primary)",
                fontFamily: "Quicksand, sans-serif",
                border: "1px solid var(--st-border-light)",
              }}
            >
              Contact Support
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Quick Links */}
        <div className="max-w-3xl mx-auto px-4 mt-8">
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => navigate("/terms")}
              className="text-xs px-3 py-1.5 rounded-lg transition-all hover:opacity-80"
              style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
            >
              Terms of Service
            </button>
            <span style={{ color: "var(--st-border-light)" }}>|</span>
            <button
              onClick={() => navigate("/privacy")}
              className="text-xs px-3 py-1.5 rounded-lg transition-all hover:opacity-80"
              style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
            >
              Privacy Policy
            </button>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default HelpPage;
